export interface WomenProduct {
  id: string;
  name: string;
  category: 'dresses' | 'dupatta' | 'kurthi' | 'palazoo' | 'sarees';
  price: number;
  description: string;
  imageUrl: string;
  availableSizes: string[];
  availableColors: string[];
}

// Generate a random price between 400-1000, weighted mostly below 600
const generatePrice = (_category: string, index: number): number => {
  // Use index as seed for deterministic but varied prices
  const seed = (index * 37 + 13) % 100;
  if (seed < 55) return 400 + (seed % 10) * 20;        // 400–580 (55% of products)
  if (seed < 80) return 580 + (seed % 8) * 25;         // 580–755 (25% of products)
  return 760 + (seed % 10) * 24;                        // 760–976 (20% of products)
};

// Helper function to generate description based on name and category
const generateDescription = (name: string, category: string): string => {
  const categoryDescriptions: Record<string, string> = {
    'dresses': 'Elegant dress perfect for special occasions and everyday wear',
    'dupatta': 'Beautiful dupatta to complement your ethnic wear',
    'kurthi': 'Traditional and contemporary kurthi for comfortable styling',
    'palazoo': 'Stylish palazzo set for modern ethnic fashion',
    'sarees': 'Traditional saree for weddings and festive occasions'
  };
  
  const baseDesc = categoryDescriptions[category] || 'Beautiful fashion item';
  
  // Add some keywords based on the name
  if (name.toLowerCase().includes('floral')) return `Floral print ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('embroidered')) return `Embroidered ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('designer')) return `Designer ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('party')) return `Party wear ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('traditional')) return `Traditional ${baseDesc.toLowerCase()}`;
  
  return baseDesc;
};

// Helper function to generate sizes based on category
const generateSizes = (category: string): string[] => {
  if (category === 'dupatta' || category === 'sarees') {
    return ['One Size'];
  }
  return ['S', 'M', 'L', 'XL'];
};

// Helper function to generate colors based on category and index
const generateColors = (category: string, index: number): string[] => {
  const colorPools: Record<string, string[][]> = {
    'dresses': [
      ['Red', 'Blue', 'Black'],
      ['Pink', 'White', 'Green'],
      ['Blue', 'Purple', 'Yellow'],
      ['Black', 'Red', 'Gold'],
      ['White', 'Pink', 'Silver']
    ],
    'dupatta': [
      ['Multicolor'],
      ['Blue', 'Red'],
      ['Green', 'Yellow'],
      ['Pink', 'Purple'],
      ['White', 'Gold']
    ],
    'kurthi': [
      ['White', 'Blue', 'Pink'],
      ['Black', 'Red', 'Green'],
      ['Blue', 'Purple', 'Yellow'],
      ['Pink', 'White', 'Orange'],
      ['Green', 'Blue', 'Brown']
    ],
    'palazoo': [
      ['Pastel Pink', 'Pastel Blue'],
      ['Multicolor'],
      ['Blue', 'Green'],
      ['Red', 'Black'],
      ['Pink', 'Purple']
    ],
    'sarees': [
      ['Red', 'Green', 'Blue'],
      ['Pink', 'Blue', 'Purple'],
      ['Gold', 'Silver', 'Maroon'],
      ['White', 'Cream', 'Beige'],
      ['Orange', 'Yellow', 'Brown']
    ]
  };
  
  const pool = colorPools[category] || [['Blue', 'Red', 'Green']];
  return pool[index % pool.length];
};

// Image data for each category
const categoryImages: Record<string, string[]> = {
  'dresses': [
    'A-Line Flowy Gown.jpg',
    'A-Line Midi Dress.jpg',
    'Anarkali Style Dress.jpg',
    'Bodycon Party Dress.jpg',
    'Buy Homdor Prom Dress Long Split Off Ball Gown.jpg',
    'Cape Style Dress.jpg',
    'Cape Style Gown.jpg',
    'Casual Shirt Dress.jpg',
    'Classic Black Gown.jpg',
    'cotton_wrap_dress.jpg',
    'Denim Shirt Dress.jpg',
    'Designer_Sharara_Suit.jpg',
    'Elegant Evening Gown.jpg',
    'Evening Gown for Women.jpg',
    'Fit and Flare Dress.jpg',
    'Floral Maxi Gown.jpg',
    'Georgette_Anarkali_Dress.jpg',
    'Girlsshop Women Elegant Satin Ball Gown Sweetheart.jpg',
    'Halter Neck Dress.jpg',
    'High-Low Hem Dress.jpg',
    'Indo Western Gown.jpg',
    'Long Bridal Gown.jpg',
    'long_anarkali_gown.jpg',
    'maxi_floral_dress.png',
    'Net Embroidered Dupatta.jpg',
    'Off Shoulder Gown.jpg',
    'Peplum Dress.jpg',
    'Satin Slip Dress.jpg',
    'Sequin Work Gown.jpg',
    'Sequin_Party_Wear_Gown.jpg',
    'silk_embroidered_gown.jpg',
    'Smocked Waist Dress.jpg',
    'Tiered Ruffle Dress.jpg',
    'Tiered_Ruffle_Maxi_Dress.jpg',
    'Velvet Evening Gown.jpg',
    'Velvet Party Dress.jpg',
    'Wrap Dress.jpg'
  ],
  'dupatta': [
    'bandhani_dhuppata.jpg',
    'block_printed_dhuppata.jpg',
    'Chiffon Dupatta.jpg',
    'Chikankari Dupatta.jpg',
    'Cotton Printed Dupatta.jpg',
    'Georgette Dupatta.jpg',
    'Ikat Dupatta.jpg',
    'Kalamkari Dupatta.jpg',
    'Kota Doria Dupatta.jpg',
    'Leheriya Dupatta.jpg',
    'Linen Dupatta.jpg',
    'Mirror Work Dupatta.jpg',
    'Organza Dupatta.jpg',
    'Phulkari Dupatta.jpg',
    'Sequins Work Dupatta.jpg',
    'Silk Banarasi Dupatta.jpg',
    'Tissue Dupatta.jpg',
    'Velvet Dupatta.jpg'
  ],
  'kurthi': [
    'a-line_floral_kurta.jpg',
    'Angrakha Style Kurta.jpg',
    'Asymmetrical Kurta.jpg',
    'black_printed_kurta.jpg',
    'Cape Style Kurta.jpg',
    'chikankari_cotton_kurti.jpg',
    'emboridered_printed_kurti.jpg',
    'High-Low Hem Kurta.jpg',
    'Indo-Western Fusion Kurta.jpg',
    'Jacket Style Kurta.jpg',
    'Kaftan_Style_Kurta.jpg',
    'Mirror_Work_Kurta_Set.jpg',
    'Panelled Kurta.jpg',
    'Peplum_Kurti_with_Sharara.jpg',
    'Pintuck Detail Kurta.jpg',
    'printed_anarkali_kurta.jpg',
    'Slit Style Kurta.jpg',
    'straight_fit_kurta.webp',
    'Tiered Kurta.jpg'
  ],
  'palazoo': [
    'Bandhani Print Palazzo Set.jpg',
    'Chikankari Palazzo Set.jpg',
    'festive_palazzo_set.jpg',
    'Georgette Palazzo Set.jpg',
    'Indo-Western Palazzo Set.jpg',
    'Jacket Style Palazzo Set.jpg',
    'Mirror Work Palazzo Set.jpg',
    'palazzo_kurti_set.jpg',
    'Party Wear Palazzo Set.jpg',
    'Pastel Designer Palazzo Set.jpg',
    'printed_palazzo_set.jpg',
    'Silk Blend Palazzo Set.jpg'
  ],
  'sarees': [
    'Banarasi Silk Saree.jpg',
    'Bandhani Print Saree.jpg',
    'Chiffon Floral Saree.jpg',
    'Cotton Handloom Saree.jpg',
    'Georgette Embroidered Saree.jpg',
    'Ikat Cotton Saree.jpg',
    'Kalamkari Print Saree.jpg',
    'Kanjivaram Silk Saree.jpg',
    'Linen Saree with Contrast Border.jpg',
    'Net Saree with Stone Work.jpg',
    'Organza Designer Saree.jpg',
    'Organza_Saree_with_Embroidery.jpg',
    'Ruffle Saree (2).jpg',
    'Ruffle_Saree.jpg',
    'Satin Silk Saree.jpg',
    'Sequin Party Wear Saree.jpg',
    'Tissue Silk Saree.jpg'
  ]
};

// Generate product data for ALL images
const generateAllWomenProducts = (): WomenProduct[] => {
  const products: WomenProduct[] = [];
  let productId = 1;

  Object.entries(categoryImages).forEach(([category, images]) => {
    images.forEach((imageName, index) => {
      // Clean up the name for display
      const cleanName = imageName
        .replace(/\.[^/.]+$/, '') // Remove extension
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letters
      
      products.push({
        id: `w${productId++}`,
        name: cleanName,
        category: category as 'dresses' | 'dupatta' | 'kurthi' | 'palazoo' | 'sarees',
        price: generatePrice(category, index),
        description: generateDescription(cleanName, category),
        imageUrl: `/assets/images/womens/${category}/${imageName}`,
        availableSizes: generateSizes(category),
        availableColors: generateColors(category, index)
      });
    });
  });

  return products;
};

// Generate ALL women's products
export const womensProducts: WomenProduct[] = generateAllWomenProducts();

// Get all products
export const getAllWomenProducts = (): WomenProduct[] => {
  return womensProducts;
};

// Get products by category
export const getWomenProductsByCategory = (category: string): WomenProduct[] => {
  if (category === 'all') {
    return womensProducts;
  }
  return womensProducts.filter(product => product.category === category);
};

// Get product by ID
export const getWomenProductById = (id: string): WomenProduct | undefined => {
  return womensProducts.find(product => product.id === id);
};

// Get all categories
export const getAllCategories = (): string[] => {
  const categories = womensProducts.map(product => product.category);
  return ['all', ...Array.from(new Set(categories))];
};

// Get category display name
export const getCategoryDisplayName = (category: string): string => {
  const displayNames: Record<string, string> = {
    'all': 'All Products',
    'dresses': 'Dresses',
    'dupatta': 'Dupatta',
    'kurthi': 'Kurthi',
    'palazoo': 'Palazzo',
    'sarees': 'Sarees'
  };
  return displayNames[category] || category;
};

// Get products count by category
export const getProductsCountByCategory = (): Record<string, number> => {
  const counts: Record<string, number> = {
    'all': womensProducts.length
  };
  
  womensProducts.forEach(product => {
    counts[product.category] = (counts[product.category] || 0) + 1;
  });
  
  return counts;
};